import { useState, useCallback, useMemo, type ReactNode } from 'react';
import { type Locale, defaultLocale, translate, localeNames } from '@/i18n';
import { I18nContext } from './I18nContext';

const STORAGE_KEY = 'yoctohanuman-web-locale';
const SUPPORTED_LOCALES: readonly Locale[] = ['ja', 'en', 'es', 'ko', 'zh'];

function isSupportedLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

function getInitialLocale(): Locale {
  if (typeof window === 'undefined') {
    return defaultLocale;
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && isSupportedLocale(stored)) {
    return stored;
  }

  const browserLang = window.navigator.language.split('-')[0];
  if (isSupportedLocale(browserLang)) {
    return browserLang;
  }

  return defaultLocale;
}

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    window.localStorage.setItem(STORAGE_KEY, newLocale);
    document.documentElement.lang = newLocale;
  }, []);

  const t = useCallback(
    (key: string, variables?: Record<string, string | number>): string => translate(locale, key, variables),
    [locale]
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, localeNames }),
    [locale, setLocale, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
